import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { EmptyError, lastValueFrom, Subject } from 'rxjs';
import { ClaimDocumentFileType, ClaimDocumentUpload } from '../../claim-insurance.model';
import { AccidentsAndIncidentsService } from '../../../../../services/accidents-and-incidents.service';
import { ErrorHandlerService } from '../../../../../../../../_shared/services/error-handler.service';
import {
  areUploadsComplete,
  buildTempAttachmentFormData,
  createAttachmentUpload,
  getFileExtension,
  isDuplicateAttachment,
  markUploadFailed,
  markUploadSucceeded,
  trackUploadProgress
} from '../../../../../helpers/incident-attachment-upload.helper';

@Component({
  selector: 'app-documents-tab',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './documents-tab.component.html',
  styleUrl: './documents-tab.component.scss'
})
export class DocumentsTabComponent implements OnDestroy {
  @Input() assetId: number | null = null;
  @Input() uploadedDocuments: ClaimDocumentUpload[] = [];
  @Output() documentsChange = new EventEmitter<ClaimDocumentUpload[]>();
  // Not named `cancel`: Angular also binds that name to the native event, which the file input fires when its picker is dismissed
  @Output() cancelled = new EventEmitter<void>();
  @Output() save = new EventEmitter<ClaimDocumentUpload[]>();

  private readonly transloco: TranslocoService = inject(TranslocoService);
  private accidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private errorHandlerService = inject(ErrorHandlerService);

  private readonly fileTypes: ClaimDocumentFileType[] = ['pdf', 'png', 'jpg', 'jpeg'];
  // The file type only picks the document icon, so anything unrecognised shows the PDF one
  private readonly defaultFileType: ClaimDocumentFileType = 'pdf';
  private readonly uploadDateLocale = 'en-US';

  attachmentError: string = '';
  private cancelSubject = new Subject<void>();

  get areDocumentsReady(): boolean {
    return areUploadsComplete(this.uploadedDocuments);
  }

  get isSaveDisabled(): boolean {
    return this.uploadedDocuments.length === 0 || !this.areDocumentsReady;
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      input.value = '';

      if (!this.canAddFile(file)) {
        return;
      }

      const upload = this.createDocumentUpload(file);
      this.uploadedDocuments = [...this.uploadedDocuments, upload];
      this.documentsChange.emit(this.uploadedDocuments);
      void this.uploadTempDocument(upload);
    }
  }

  onDownloadDocument(doc: ClaimDocumentUpload): void {
    const url = URL.createObjectURL(doc.file);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.name;
    link.click();
    URL.revokeObjectURL(url);
  }

  onDeleteDocument(doc: ClaimDocumentUpload): void {
    this.uploadedDocuments = this.uploadedDocuments.filter((uploadedDocument) => uploadedDocument.reference !== doc.reference);
    this.attachmentError = '';
    this.documentsChange.emit(this.uploadedDocuments);
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onSave(): void {
    this.save.emit(this.uploadedDocuments);
  }

  private canAddFile(file: File): boolean {
    this.attachmentError = '';

    if (isDuplicateAttachment(this.uploadedDocuments, file)) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.duplicateFile');
      return false;
    }

    return true;
  }

  private createDocumentUpload(file: File): ClaimDocumentUpload {
    return {
      ...createAttachmentUpload(file),
      type: this.getFileType(file.name),
      date: new Date().toLocaleDateString(this.uploadDateLocale)
    };
  }

  private async uploadTempDocument(doc: ClaimDocumentUpload): Promise<void> {
    if (!this.assetId) {
      markUploadFailed(doc);
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.selectAssetFirst');
      return;
    }

    try {
      await lastValueFrom(
        this.accidentsAndIncidentsService
          .uploadTempIncidentAttachment(buildTempAttachmentFormData(this.assetId, doc), this.cancelSubject)
          .pipe(trackUploadProgress(doc))
      );

      markUploadSucceeded(doc);
      this.documentsChange.emit(this.uploadedDocuments);
    } catch (error) {
      // Upload was cancelled because the modal closed, so there's nothing to report
      if (error instanceof EmptyError) {
        return;
      }

      markUploadFailed(doc);
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.uploadFailed');
      this.documentsChange.emit(this.uploadedDocuments);
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  private getFileType(fileName: string): ClaimDocumentFileType {
    const extension = getFileExtension(fileName)?.slice(1).toLowerCase();
    return this.fileTypes.find((fileType) => fileType === extension) ?? this.defaultFileType;
  }
}
